/**
 * ContractorDetail.tsx — F11 T11.2b page detail kontrahenta.
 *
 * Routing: /contractors/new (mode=create) | /contractors/:id (mode=edit).
 *
 * Tryb OSADZONY (`embedded`, okno wyboru kontrahenta — decyzja spotkania FK 31.08):
 *   gdy prop `embedded` jest podany, komponent działa jako formatka w modalu
 *   (`ContractorFormModal`) zamiast strony pod route'em — `id`/`isCreate` biorą się z
 *   `embedded` (nie z `useParams`), nawigacja jest zastąpiona callbackami
 *   (`onSaved`/`onCancel`), a synchronizacja zakładki z URL jest wyłączona (bo URL
 *   należy wtedy do strony-gospodarza, np. faktury). Gdy `embedded` NIE jest podany —
 *   zachowanie strony jest IDENTYCZNE jak dotąd (route-based), zero regresji.
 *
 * Layout (create=edit parity — mirror ContractDetail/SaleInvoiceDetail):
 *   header (nazwa + status + NIP + akcje Edit/Save/Cancel/Delete)
 *   Bez aktywnego layoutu — sam ContractorDataTab (create i edit jednakowo).
 *   Z layoutem — RecursiveFormRenderer WSPÓLNY dla create i edit: czyta
 *     layoutConfig z F12.N buildera, tabRenderers mapuje slug → komponent.
 *     Wszystkie zakładki formularza (data, addresses, bank_accounts,
 *     company_access) są dostępne OD RAZU w trybie create —
 *     przełączanie zakładek NIE wykonuje żadnego zapytania HTTP. Tabele
 *     dodatkowe (additional_view/satellite_view) są buforowane (bez HTTP,
 *     delty jadą w POST/PATCH — patrz saveAggregate). Zakładki wymagające
 *     zapisanego rekordu (contacts, audit_log, approvals)
 *     pozostają gated `!isCreate && id`. Pola dodatkowe NIE mają własnej zakładki:
 *     pojedyncze pola układa w „Dane" silnik formatek.
 *
 * Tryb edit: toggle Edit/Save w header. Anuluj = reset do server state. Save = PATCH.
 * Tryb create: formularz z wartościami domyślnymi pól (Ustawienia kartoteki → Pola; pola widać
 *   dopiero po ich wczytaniu), edit mode od razu; JEDEN POST na "Zapisz"
 *   (pola + extValues + additionalTables w tym samym body, saveAggregate) →
 *   po sukcesie redirect do /contractors/:newId; auto-NIP-check.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { ArrowLeft, Pencil, Save, Trash2, X as XIcon } from 'lucide-react';

import { AdditionalTableTab } from '@/components/additional/AdditionalTableTab';
import { AdditionalViewTab } from '@/components/additional/AdditionalViewTab';
import { AddressesDynamicTab } from '@/components/addresses/AddressesDynamicTab';
import { AttachmentsSection } from '@/components/attachments/AttachmentsSection';
import { buildDocumentPreviewContextRenderer } from '@/components/attachments/DocumentPreviewContextRenderer';
import { DocumentPreviewDockHost } from '@/components/attachments/DocumentPreviewDockHost';
import { BankAccountsDynamicTab } from '@/components/bank-accounts/BankAccountsDynamicTab';
import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ContactsTab } from '@/components/contractors/ContactsTab';
import { ContractorAccessTab } from '@/components/contractors/ContractorAccessTab';
import { ContractorAuditTab } from '@/components/contractors/ContractorAuditTab';
import {
  buildContractorCoreRecordValues,
  ContractorDataTab,
  type ContractorFormData,
  type ContractorPrimaryAddress,
  EMPTY_FORM,
  EMPTY_PRIMARY_ADDRESS,
} from '@/components/contractors/ContractorDataTab';
import {
  contractorEngineConfig,
  type ContractorExtra,
} from '@/components/contractors/contractorEngineConfig';
import { ContractorLogoEditor } from '@/components/contractors/ContractorLogoEditor';
import {
  ContractorPrimaryAddressSection,
  parseHiddenPrimaryAddressFields,
  POLAND_COUNTRY_CODE,
} from '@/components/contractors/ContractorPrimaryAddressField';
import {
  type ExistingContractorMatch,
  ExistingContractorModal,
} from '@/components/contractors/ExistingContractorModal';
import {
  type ContextRenderers,
  RecursiveFormRenderer,
} from '@/components/form/RecursiveFormRenderer';
import { SaveFirstNotice } from '@/components/form/SaveFirstNotice';
import { IconButton } from '@/components/IconButton';
import { collectEngineFieldErrors } from '@/components/kartoteki/engine/engine-config';
import { useKartotekaRenderField } from '@/components/kartoteki/engine/useKartotekaRenderField';
import { parseHiddenAddressColumns } from '@/components/modules/layout-elements/ContractorAddressesViewElement';
import { parseHiddenBankAccountColumns } from '@/components/modules/layout-elements/ContractorBankAccountsViewElement';
import { parseProcessViewElementConfig } from '@/components/modules/layout-elements/ProcessViewElement';
import { ProcessActionButton } from '@/components/processes/ProcessActionButton';
import { ProcessApprovalBanner } from '@/components/processes/ProcessApprovalBanner';
import { ProcessAutostartIndicator } from '@/components/processes/ProcessAutostartIndicator';
import { ProcessHistoryTab } from '@/components/processes/ProcessHistoryTab';
import { NewRecordDefaultsStatus } from '@/components/record-defaults/NewRecordDefaultsStatus';
import {
  parseRecordLinksElementConfig,
  RecordLinksSection,
} from '@/components/record-links/RecordLinksSection';
import { RecordRunLockBanner } from '@/components/record-procedures/RecordRunLockBanner';
import {
  collectSqlButtonLabels,
  parseSqlButtonSettings,
} from '@/components/record-procedures/sql-button-settings';
import { SqlButton } from '@/components/record-procedures/SqlButton';
import { Spinner } from '@/components/Spinner';
import { type CompanyPickerOption } from '@/components/ui/CompanyPicker';
import { useToast } from '@/contexts/ToastContext';
import { invalidateRecordInstances } from '@/hooks/useActiveInstanceForRecord';
import {
  type FlattenedAdditionalTables,
  useAdditionalTableDeltas,
} from '@/hooks/useAdditionalTableDeltas';
import { useControlWarning } from '@/hooks/useControlWarning';
import { useFieldErrors } from '@/hooks/useFieldErrors';
import {
  applyCoreDefaults,
  dateAsDayStartIso,
  type NewRecordDefaults,
  numberAsString,
  useNewRecordDefaults,
} from '@/hooks/useNewRecordDefaults';
import { useRecordExtValues } from '@/hooks/useRecordExtValues';
import { useRecordProcedureRuns } from '@/hooks/useRecordProcedureRuns';
import { useRecordProcessAutostart } from '@/hooks/useRecordProcessAutostart';
import { useSaveBeforeDecision } from '@/hooks/useSaveBeforeDecision';
import { useSystemModuleActiveLayout } from '@/hooks/useSystemModuleActiveLayout';
import { useWithMenuItem } from '@/hooks/useWithMenuItem';
import { type AdditionalParentValues } from '@/lib/additional-tables/parent-field-values';
import { buildAdditionalViewRecordValues } from '@/lib/additional-views/record-values';
import { api } from '@/lib/api';
import { contractorSaveErrorMessage } from '@/lib/contractors/contractor-save-errors';
import { extractErrorCode } from '@/lib/errors';
import { useSystemModuleFields } from '@/lib/ext-list-columns';
import { parseSlugList } from '@/lib/kartoteki/formatka-order';
import { CONTRACTOR_MODULE_CODE } from '@/lib/processes/module-routes';
import { useActiveCompanyScope } from '@/lib/use-active-company';
import { useMyModules } from '@/lib/use-my-modules';
import { useCompanies } from '@/lib/use-permissions';
import { buildFieldIssueMessages, resolveFieldLabel } from '@/lib/validation-toast';

interface ContractorPayload {
  contractor: ContractorFormData & {
    id: string;
    homeCompanyId: string;
    gcrecord: string | null;
    logoUrl?: string | null;
    // Źródło pochodzenia rekordu (np. 'symfonia'). Pozycja i status z Symfonii to teraz pola
    // formularza (ContractorFormData, read-only), hydratowane z wartości AKTYWNEJ firmy — nie osobne pola tu.
    externalSource?: string | null;
    // Surowe wartości pól dodatkowych — dołączone przez serwer do odpowiedzi GET rekordu.
    extValues?: Record<string, unknown>;
  };
  // WF NEW 2026-08-13 (punkt 1) — adres macierzysty zwracany osobno (nie na wierszu kontrahenta,
  // bo żyje w polimorficznej tabeli adresów). Null gdy kontrahent nie ma adresu głównego.
  primaryAddress?: (ContractorPrimaryAddress & { id: string }) | null;
  homeCompanyName?: string;
  // Prawa akcji per rekord (E3b) — liczone silnikiem z kategorią; front chowa Edytuj/Usuń.
  permissions?: { canEdit: boolean; canDelete: boolean };
}

// ─── F18 T18.4 paczka 4 — MF Biała Lista lookup ─────────────────────────────
// Przeniesione z ContractorDataTab (silnik formatek — kanał `extra` niesie
// tylko callbacki/stan, nie logikę; strona jest jedynym właścicielem `form`).

interface MfLookupResponse {
  found: boolean;
  source: 'mf' | 'cache';
  company: {
    name: string;
    regon: string | null;
    krs: string | null;
    statusVat: string | null;
    vatInactive: boolean;
    // Adres rejestrowy z Białej Listy MF — backend go parsuje i zwraca; wypełnia
    // adres macierzysty (`form.primaryAddress`). Null gdy MF nie podało adresu.
    address: {
      street: string | null;
      buildingNo: string | null;
      apartmentNo: string | null;
      postalCode: string | null;
      city: string | null;
      parseFailed: boolean;
    } | null;
  } | null;
}

/** Mapuje MF statusVat ('Czynny'|'Zwolniony'|'Niezarejestrowany') → nasz enum vatStatus. */
function mapMfVatStatus(statusVat: string | null): ContractorFormData['vatStatus'] | null {
  switch (statusVat) {
    case 'Czynny':
      return 'active';
    case 'Zwolniony':
      return 'exempt';
    case 'Niezarejestrowany':
      return 'removed';
    default:
      return null;
  }
}

/**
 * Formularz NOWEGO kontrahenta: wartości startowe z kodu (`EMPTY_FORM`) nadpisane wartościami
 * domyślnymi pól (Ustawienia kartoteki → Pola). Kształt dwóch pól różni się od kształtu wartości
 * domyślnej: limit kredytowy formularz trzyma jako tekst (liczba → tekst), a datę zgody GDPR
 * jako pełny znacznik czasu (serwer przyjmuje tylko taki; renderer zapisuje `…T00:00:00.000Z`).
 * Termin płatności zostaje liczbą.
 */
function buildCreateForm(core: NewRecordDefaults['core']): ContractorFormData {
  return applyCoreDefaults(EMPTY_FORM, core, {
    creditLimit: numberAsString,
    gdprConsentAt: dateAsDayStartIso,
  });
}

/**
 * Sterowanie trybem osadzonym (okno wyboru kontrahenta) — patrz JSDoc pliku. `onSaved`
 * dostaje id zapisanego rekordu (create i edit), `onCancel` zamyka okno bez zapisu
 * (Anuluj / usunięcie / strzałka wstecz). Brak propa = tryb strony (route-based).
 */
export interface ContractorDetailEmbeddedControls {
  mode: 'create' | 'edit';
  /** Wymagane w trybie edit — kontrahent do edycji. */
  contractorId?: string;
  /** Firma-scope dla trybu create (prefill właściciela); pomijane w edit. */
  companyId?: string;
  onSaved: (id: string) => void;
  onCancel: () => void;
}

export interface ContractorDetailProps {
  embedded?: ContractorDetailEmbeddedControls;
}

export function ContractorDetail({ embedded }: ContractorDetailProps = {}) {
  const { t } = useTranslation();
  const routeParams = useParams<{ id?: string }>();
  // Tryb osadzony bierze id/isCreate z propsów; tryb strony — z route'a (bez zmian).
  const id = embedded
    ? embedded.mode === 'edit'
      ? embedded.contractorId
      : undefined
    : routeParams.id;
  const isCreate = embedded ? embedded.mode === 'create' : !id || id === 'new';
  const navigate = useNavigate();
  // Powrót na listę niesie jej kontekst — patrz useWithMenuItem.
  const withMenuItem = useWithMenuItem();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [searchParams, setSearchParams] = useSearchParams();
  // F12.N E1.T5b: layout w nowym kształcie + role context dla per-rola visibility.
  // Hook robi migrację w locie jeśli backend zwróci stary kształt (do deploy E1.T7).
  const { layoutConfig, myRoleId, isSuperUser, previewDockEnabled } =
    useSystemModuleActiveLayout('contractors');

  // F17 T17.1.7/T17.1.8: banner akceptacji na karcie — tylko gdy tenant ma aktywny
  // moduł Procesy (gating jak w ModuleGuard, ale bez pełnoekranowego fallbacku).
  const { data: myModules } = useMyModules();
  const processesActive = myModules?.subscriptions.some((s) => s.code === 'processes') ?? false;

  // F17 follow-up — wskaźnik „proces się uruchamia…" po create z automatyzacją
  // start_approval (uzbrajany hintem `processAutoStarting` z odpowiedzi create, patrz
  // saveMutation.onSuccess niżej). Wołany bezwarunkowo — create→detail nie remontuje tę
  // stronę (ten sam element w Route, bez `key`), więc hook musi żyć przez całą transformację.
  const autostart = useRecordProcessAutostart(CONTRACTOR_MODULE_CODE, id ?? '');

  // Przycisk SQL (klocek `sql_button`): zadania wykonywane w tle na tym kontrahencie. Stan (aktywne
  // zadanie + ostatnie per procedura) odpytywany co ~2 s tylko przy aktywnym zadaniu; po jego końcu
  // komunikat w rogu i odświeżenie rekordu (zapytanie mogło go zmienić). Przy tworzeniu brak rekordu.
  // Napisy przycisków z układu służą komunikatom i paskowi blokady (zadanie zna tylko nazwę procedury).
  const sqlButtonLabels = useMemo(() => collectSqlButtonLabels(layoutConfig), [layoutConfig]);
  const runs = useRecordProcedureRuns('contractor', isCreate ? undefined : id, {
    labels: sqlButtonLabels,
    onFinished: () => {
      void queryClient.invalidateQueries({ queryKey: ['contractor', id] });
      void queryClient.invalidateQueries({ queryKey: ['contractors'] });
      void queryClient.invalidateQueries({ queryKey: ['additional-records'] });
      void queryClient.invalidateQueries({ queryKey: ['addresses', 'contractor', id] });
      void queryClient.invalidateQueries({ queryKey: ['contractor-contacts', id] });
      void queryClient.invalidateQueries({ queryKey: ['system-module-audit', 'contractors', id] });
    },
  });
  // Przycisk kliknięty na NOWYM rekordzie: najpierw utworzenie (zwykły zapis), potem zadanie z id
  // świeżo utworzonego kontrahenta, dopiero potem przejście pod jego adres (patrz `saveMutation.onSuccess`).
  // `settle` kończy oczekiwanie przycisku (kręciołek), także przy porażce zapisu; `label` to napis klikniętego
  // przycisku (do komunikatu „Zadanie … zostało zlecone" w oknie osadzonym).
  const pendingCreateRunRef = useRef<{
    procedureId: string;
    label: string;
    settle: () => void;
  } | null>(null);

  // Deep-link `?tab=foo` (seed-slug) — fallback do 'data' przy braku param'a.
  // Walidacja istnienia zakładki w widoczności jest po stronie RecursiveFormRenderer:
  // jeśli `tab` nie pasuje do żadnej widocznej zakładki, atom Tabs nie pokazuje contentu.
  // Tryb osadzony: zakładka żyje wyłącznie w stanie lokalnym (URL należy do strony-
  // gospodarza, np. faktury — nie wolno go czytać ani nadpisywać).
  const initialTab = embedded ? 'data' : (searchParams.get('tab') ?? 'data');
  const [tab, setTabRaw] = useState<string>(initialTab);

  // Sync URL → state (React Router 6 nie remountuje instancji na SPA navigate
  // /new → /:id — ten sam `element` w obu Routes, `useState` NIE re-runs). W trybie
  // osadzonym pomijamy — brak własnego URL.
  useEffect(() => {
    if (embedded) return;
    const urlTab = searchParams.get('tab') ?? 'data';
    if (urlTab !== tab) setTabRaw(urlTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, embedded]);

  // Create=edit parity fix (mirror ContractDetail/SaleInvoiceDetail): zwykła
  // zmiana zakładki, bez żadnego niejawnego zapisu — w trybie create wszystkie
  // zakładki formularza są dostępne od razu, zapis następuje wyłącznie na klik
  // „Zapisz" (patrz saveMutation).
  function setTab(next: string) {
    setTabRaw(next);
    // Tryb osadzony nie ma własnego URL — nie dotykamy query strony-gospodarza.
    if (embedded) return;
    const params = new URLSearchParams(searchParams);
    if (next === 'data') params.delete('tab');
    else params.set('tab', next);
    setSearchParams(params, { replace: true });
  }
  // Tryb osadzony (okno wyboru): otwarcie formatki = intencja edycji — startujemy od
  // razu w trybie edycji (create i edit), bez dodatkowego kliku „Edytuj".
  const [editing, setEditing] = useState(isCreate || !!embedded);
  const [form, setForm] = useState<ContractorFormData>(EMPTY_FORM);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmUnlink, setConfirmUnlink] = useState(false);
  const [nipConflict, setNipConflict] = useState<ExistingContractorMatch | null>(null);
  const [nipModalOpen, setNipModalOpen] = useState(false);
  // Silnik formatek — kanał `extra` kontrahenta (onBlur check-nip self-service
  // share + MF Biała Lista lookup), przeniesione z ContractorDataTab. Nazwy
  // stanu NIE kolidują z `nipConflict`/`nipModalOpen` powyżej (409 NIP_TAKEN
  // przy zapisie) — to DWA NIEZALEŻNE modale, oba `<ExistingContractorModal>`.
  const [nipMatch, setNipMatch] = useState<ExistingContractorMatch | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [mfLookupLoading, setMfLookupLoading] = useState(false);
  // Podswietlenie pol odrzuconych przez backendowy VALIDATION_ERROR (czerwona
  // ramka, bez tekstu — komunikat zostaje w toascie). Patrz onError ponizej.
  const fe = useFieldErrors();

  // Atomic-save refactor (mirror ContractDetail/SaleInvoiceDetail): bufor delt
  // tabel dodatkowych (i szkiców add-row) we wspólnym hooku — kontrakt freshness
  // `getBufferedValue` w nagłówku `useAdditionalTableDeltas.ts`.
  const {
    handleAdditionalDeltasChange,
    getBufferedValue,
    flattenAdditionalDeltas,
    buildAdditionalParentValues,
    resetAdditionalDeltas,
    getAddRowDraft,
    handleAddRowDraftChange,
    resetAddRowDrafts,
  } = useAdditionalTableDeltas();

  const detailQ = useQuery({
    queryKey: ['contractor', id],
    queryFn: async () => {
      const res = await api.get<ContractorPayload>(`/contractors/${id}`);
      return res.data;
    },
    enabled: !isCreate,
  });

  // Wartości domyślne pól (Ustawienia kartoteki → Pola) dla formularza nowego kontrahenta — także
  // w oknie osadzonym („Dodaj nowego kontrahenta"). Pola pokazujemy dopiero po ich wczytaniu
  // (patrz `NewRecordDefaultsStatus` niżej).
  const recordDefaults = useNewRecordDefaults('contractors', isCreate);

  // F12.M D.2: state ext-pól w zakładce „Dane" — kontrolowany z tej strony, zasilany wartością
  // z odpowiedzi GET rekordu (`contractor.extValues`, bez osobnego żądania). Pełny kontrakt
  // synchronizacji (w tym ochrona niezapisanych edycji przed odświeżeniem w tle) w nagłówku
  // `useRecordExtValues.ts`. Przy tworzeniu „wartością serwera" są wartości domyślne pól
  // dodatkowych.
  const { extValues, handleExtChange, markSaved, snapshotEdits, discardEdits } = useRecordExtValues(
    {
      recordId: isCreate ? undefined : id,
      serverExtValues: isCreate ? recordDefaults.ext : detailQ.data?.contractor.extValues,
      serverVersion: detailQ.dataUpdatedAt,
    },
  );

  // Prawa akcji per rekord (E3b) — serwer liczy je silnikiem z kategorią rekordu; front chowa
  // Edytuj/Usuń, gdy brak prawa. Brak danych (ładowanie) = pokazujemy (serwer i tak egzekwuje).
  const canEditRecord = detailQ.data?.permissions?.canEdit ?? true;
  const canDeleteRecord = detailQ.data?.permissions?.canDelete ?? true;

  // Self-service share (ExistingContractorModal) potrzebuje KONKRETNEJ firmy dla body
  // POST /company-access. `nipConflict` dzis powstaje wylacznie z 409 NIP_TAKEN zwracanego
  // przez POST /contractors (create) — wiec preferujemy homeCompanyId juz zaladowanego
  // rekordu (tryb edit, gdyby kiedys ten sam konflikt zaczal wystepowac tez na PATCH), a
  // gdy go nie ma (create, jeszcze bez zapisanego rekordu) — spadamy na aktywny kontekst
  // firmy. W trybie "Wszystkie firmy" bez zapisanego rekordu (companyId === null) modal
  // jest celowo NIE renderowany nizej.
  const activeCompanyScope = useActiveCompanyScope();
  // Tryb osadzony: firma dokumentu (`embedded.companyId`) jest lepszym fallbackiem niż
  // aktywny kontekst (okno wyboru na dokumencie zna firmę wprost).
  const existingContractorCompanyId =
    detailQ.data?.contractor.homeCompanyId ?? embedded?.companyId ?? activeCompanyScope.companyId;

  /**
   * Onblur self-service share check (przeniesione z ContractorDataTab) — działa
   * TYLKO w create (jeszcze bez zapisanego rekordu, więc `check-nip` ma sens).
   * `nipMatch`/`modalOpen` state powyżej, DRUGI (niezależny) modal od `nipConflict`
   * (409 NIP_TAKEN przy zapisie, dół strony).
   */
  async function handleNipBlur() {
    if (!isCreate) return;
    const nip = form.taxId?.trim();
    if (!nip) return;
    try {
      const res = await api.get<
        | { exists: false }
        | {
            exists: true;
            contractorId: string;
            name: string;
            homeCompanyName: string;
            hasAccessFromCurrentCompany: boolean;
          }
      >(`/contractors/check-nip?taxId=${encodeURIComponent(nip)}`);
      if (res.data.exists) {
        setNipMatch({
          contractorId: res.data.contractorId,
          name: res.data.name,
          homeCompanyName: res.data.homeCompanyName,
          hasAccessFromCurrentCompany: res.data.hasAccessFromCurrentCompany,
        });
        setModalOpen(true);
      }
    } catch (err) {
      console.error('[ContractorDetail] check-nip failed:', err);
    }
  }

  /**
   * F18 T18.4 paczka 4 — lookup firmy po NIP w MF Białej Liście (autouzupełnienie),
   * przeniesione z ContractorDataTab. Wypełnia name/regon/krs/vatStatus z danych MF.
   * Toast ostrzega gdy podatnik wykreślony z VAT (vatInactive). Adresy + konta
   * bankowe MF zwraca, ale form „Dane" ich nie ma (osobne struktury) — pokazujemy
   * info w toast.
   */
  async function handleMfLookup() {
    const nip = form.taxId?.trim();
    if (!nip) return;
    setMfLookupLoading(true);
    try {
      const res = await api.get<MfLookupResponse>(
        `/contractors/lookup-mf?nip=${encodeURIComponent(nip)}`,
      );
      if (!res.data.found || !res.data.company) {
        toast.show({
          variant: 'warning',
          title: t('tenant.contractors.detail.mfLookup.notFound'),
        });
        return;
      }
      const c = res.data.company;
      // Adres rejestrowy z MF wypełnia adres macierzysty. Nadpisujemy tylko pola, które
      // MF zwróciło (puste z MF nie kasują ręcznie wpisanych); countryCode domyślnie 'PL'
      // (Biała Lista to rejestr polski).
      const nextPrimaryAddress: ContractorPrimaryAddress = c.address
        ? {
            street: c.address.street ?? form.primaryAddress.street,
            buildingNo: c.address.buildingNo ?? form.primaryAddress.buildingNo,
            apartmentNo: c.address.apartmentNo ?? form.primaryAddress.apartmentNo,
            postalCode: c.address.postalCode ?? form.primaryAddress.postalCode,
            city: c.address.city ?? form.primaryAddress.city,
            region: form.primaryAddress.region,
            countryCode: form.primaryAddress.countryCode ?? POLAND_COUNTRY_CODE,
          }
        : form.primaryAddress;
      setForm({
        ...form,
        name: c.name || form.name,
        regon: c.regon ?? form.regon,
        krs: c.krs ?? form.krs,
        vatStatus: mapMfVatStatus(c.statusVat) ?? form.vatStatus,
        primaryAddress: nextPrimaryAddress,
      });
      if (c.vatInactive) {
        toast.show({
          variant: 'error',
          title: t('tenant.contractors.detail.mfLookup.vatInactive', {
            status: c.statusVat ?? '—',
          }),
        });
      } else {
        const fromCache = res.data.source === 'cache';
        toast.show({
          variant: 'success',
          title: fromCache
            ? t('tenant.contractors.detail.mfLookup.filledFromCache')
            : t('tenant.contractors.detail.mfLookup.filled'),
        });
      }
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 429) {
        toast.show({ variant: 'error', title: t('tenant.contractors.detail.mfLookup.rateLimit') });
      } else {
        toast.show({ variant: 'error', title: t('tenant.contractors.detail.mfLookup.error') });
      }
    } finally {
      setMfLookupLoading(false);
    }
  }

  // Lista firm tenanta dla renderera `company_id` (CompanyPicker) — mirror
  // `EmployeeDataTab.tsx`'s `companiesQ`/`lookups.companies`. `GET /companies` (nie
  // `me.companies` z auth contextu, który jest zawężony do firm ZALOGOWANEGO usera) —
  // super-user bez UCA musi móc wybrać dowolną firmę tenanta przy tworzeniu.
  const companiesQ = useCompanies();
  const companyOptions: CompanyPickerOption[] = (companiesQ.data?.companies ?? []).map((c) => ({
    id: c.id,
    legalName: c.legalName,
  }));

  // Silnik formatek (keystone unifikacja-formatek, ostatni krok) — 27 pól core
  // kontrahenta przez `contractorEngineConfig`. `hasError`/`extra` niosą to, co
  // wcześniej trzymał ContractorDataTab lokalnie (podświetlenie walidacji + kanał
  // NIP) — silnik wymaga JEDNEGO kierunku przepływu (strona → runtime → renderer).
  const { renderField, fields: kartotekaFields } = useKartotekaRenderField<
    ContractorFormData,
    ContractorExtra
  >(contractorEngineConfig, {
    form,
    onChange: setForm,
    extValues,
    onExtChange: handleExtChange,
    editable: editing,
    isCreate,
    companyId: existingContractorCompanyId ?? undefined,
    hasError: (key) => !!fe.get(String(key)),
    getError: (key) => fe.get(String(key)),
    clearError: (key) => fe.clear(String(key)),
    extra: {
      onNipBlur: handleNipBlur,
      onMfLookup: handleMfLookup,
      mfLookupLoading,
      companies: companyOptions,
    },
  });

  // Wartości formularza keyed po slugu (core + ext) — do oceny warunków widoczności
  // pól/bloków z kreatora. Core buduje `buildContractorCoreRecordValues`, ext lecą
  // 1:1 (klucz ext = slug). Przekazywane do ContractorDataTab i RecursiveFormRenderer.
  const layoutFormData = useMemo<Record<string, unknown>>(
    () => ({ ...buildContractorCoreRecordValues(form), ...extValues }),
    [form, extValues],
  );

  // Wartości pól kontrahenta (core + dodatkowe) JAK NA EKRANIE, także niezapisane zmiany —
  // źródło wiązań `parent_field` słowników w wierszach tabel dodatkowych (zakładki dostają je
  // jako `parentValues`). Skalary/null. Stabilność referencji nie jest wymagana (klucz zapytania
  // o opcje to zserializowane wybrane wartości); `useMemo` tylko oszczędza przeliczanie mapy.
  const additionalOwnerValues = useMemo(
    () => buildAdditionalViewRecordValues(extValues, buildContractorCoreRecordValues(form)),
    [form, extValues],
  );

  const contractorFieldsQ = useSystemModuleFields('contractors');
  const contractorFieldLabel = (field: string) =>
    resolveFieldLabel(contractorFieldsQ.data ?? [], field);

  // Rekord, dla którego `form` jest aktualnie zhydrowany z serwera —
  // odróżnia "user edytuje TEN SAM rekord" (refetch spoza tego formularza NIE
  // MOŻE nadpisać niezapisanych zmian) od zmiany rekordu (nawigacja na inny
  // /contractors/:id musi zhydratować mimo że `editing` zostało `true` z
  // poprzedniej sesji edycji — patrz guard niżej). Pola dodatkowe (`extValues`)
  // mają własny, równoległy guard w `useRecordExtValues`.
  const hydratedRecordIdRef = useRef<string | undefined>(undefined);

  // Synchronizacja form ze server state. P4b bugfix (silent-null owner):
  // invalidateQueries(['contractor', id]) wywoływane przez INNE zakładki tej
  // strony w trakcie sesji edycji „Dane" (ContractorAccessTab transfer-ownership,
  // ContractorLogoEditor upload — oba robią refetch tego samego query key) nie
  // może cicho nadpisać niezapisanych zmian usera (np. właśnie wybranego
  // ownerUserId) danymi z serwera sprzed zapisu. Hydratacja pomijana TYLKO gdy
  // user aktywnie edytuje (`editing`) TEN SAM rekord, dla którego `form` już
  // jest zhydrowany — initial load, powrót do widoku po zapisie (editing=false,
  // ustawiane w saveMutation.onSuccess PRZED refetchem) i przejście na inny
  // rekord nadal hydratują normalnie. Tryb create (gałąź niżej) bez zmian —
  // `hydratedRecordIdRef` zresetowany do `undefined` nigdy nie zrówna się z
  // realnym id nowo utworzonego rekordu, więc hydratacja po create+redirect
  // (continue-editing) też przechodzi.
  useEffect(() => {
    if (isCreate) {
      hydratedRecordIdRef.current = undefined;
      companyPrefilledRef.current = false;
      // Wartości domyślne nadpisują wartości startowe z kodu; firma z kontekstu/okna osadzonego
      // (efekt prefill niżej, odpalany ponownie po wczytaniu wartości) — na końcu.
      setForm(buildCreateForm(recordDefaults.core));
      return;
    }
    if (editing && hydratedRecordIdRef.current === id) return;
    if (detailQ.data?.contractor) {
      const c = detailQ.data.contractor;
      hydratedRecordIdRef.current = id;
      setForm({
        name: c.name,
        // Read-only na formularzu edycji (patrz renderer `company_id` — disabled poza
        // `isCreate`); wypełnione, żeby CompanyPicker pokazywał AKTUALNĄ firmę-właściciela.
        targetCompanyId: c.homeCompanyId,
        shortName: c.shortName,
        objectType: c.objectType,
        taxId: c.taxId,
        vatEu: c.vatEu,
        regon: c.regon,
        krs: c.krs,
        status: c.status,
        email: c.email,
        phone: c.phone,
        mobile: c.mobile,
        website: c.website,
        currencyCode: c.currencyCode,
        paymentTermsDays: c.paymentTermsDays,
        creditLimit: c.creditLimit,
        isVatPayer: c.isVatPayer,
        vatStatus: c.vatStatus,
        ownerUserId: c.ownerUserId,
        assignedUserId: c.assignedUserId,
        source: c.source,
        syncExcluded: c.syncExcluded ?? false,
        symfoniaExternalId: c.symfoniaExternalId ?? null,
        symfoniaPosition: c.symfoniaPosition ?? null,
        symfoniaActive: c.symfoniaActive ?? null,
        symfoniaCompanyId: c.symfoniaCompanyId ?? null,
        industry: c.industry,
        preferredLanguage: c.preferredLanguage ?? 'pl',
        gdprConsentAt: c.gdprConsentAt ?? null,
        gdprMarketingConsent: c.gdprMarketingConsent ?? false,
        gdprDataRetentionUntil: c.gdprDataRetentionUntil ?? null,
        notes: c.notes,
        tags: c.tags ?? [],
        pkdCodes: c.pkdCodes ?? [],
        groups: c.groups ?? [],
        categoryId: c.categoryId ?? null,
        // WF NEW 2026-08-13 (punkt 1) — adres macierzysty z osobnego pola odpowiedzi GET.
        primaryAddress: detailQ.data.primaryAddress
          ? {
              street: detailQ.data.primaryAddress.street,
              buildingNo: detailQ.data.primaryAddress.buildingNo,
              apartmentNo: detailQ.data.primaryAddress.apartmentNo,
              postalCode: detailQ.data.primaryAddress.postalCode,
              city: detailQ.data.primaryAddress.city,
              region: detailQ.data.primaryAddress.region,
              countryCode: detailQ.data.primaryAddress.countryCode,
            }
          : { ...EMPTY_PRIMARY_ADDRESS },
      });
    }
    // `editing` celowo NIE jest w deps — hydratacja ma reagować na faktyczną
    // zmianę `detailQ.data` (initial load / zmiana rekordu / refetch po
    // zapisie), NIE na sam moment przełączenia editing=false (co mogłoby na
    // chwilę pokazać jeszcze nieodświeżone dane sprzed refetcha wywołanego
    // przez saveMutation.onSuccess).
    // `recordDefaults.core` zmienia referencję raz (wczytanie) — pola są wtedy jeszcze ukryte.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailQ.data, isCreate, id, recordDefaults.core]);

  // Core invariant (bez tworzenia rekordu bez jawnie wybranej konkretnej firmy) — mirror
  // EmployeeDetail.tsx. MUSI być zadeklarowany PO efekcie reset/hydratacji wyżej (kolejność
  // efektów w komponencie = kolejność ich flushowania) — inaczej `setForm(EMPTY_FORM)` z
  // resetu, wołany na TYM SAMYM initial commit w trybie create, nadpisałby prefill zrobiony
  // tutaj. W trybie jednej aktywnej firmy prefill `form.targetCompanyId` z aktywnego
  // kontekstu; w trybie "Wszystkie firmy" pole zostaje puste i user MUSI wybrać (CompanyPicker
  // w `contractorEngineConfig.tsx`), submit zablokowany w handleSave niżej.
  const companyPrefilledRef = useRef(false);
  useEffect(() => {
    if (!isCreate) return;
    if (companyPrefilledRef.current) return;
    // Tryb osadzony (okno wyboru na dokumencie): firma dokumentu jest źródłem prawdy —
    // prefill z `embedded.companyId` ma pierwszeństwo przed aktywnym kontekstem.
    const resolvedId = embedded?.companyId ?? activeCompanyScope.companyId;
    if (!embedded?.companyId && activeCompanyScope.mode !== 'company') return;
    if (!resolvedId) return;
    companyPrefilledRef.current = true;
    setForm((f) => (f.targetCompanyId ? f : { ...f, targetCompanyId: resolvedId }));
    // `recordDefaults.core`: po wczytaniu wartości domyślnych efekt resetu wyżej odbudowuje formularz
    // i zdejmuje `companyPrefilledRef` — firma (kontekst/okno osadzone) musi wejść ponownie.
  }, [
    isCreate,
    embedded?.companyId,
    activeCompanyScope.mode,
    activeCompanyScope.companyId,
    recordDefaults.core,
  ]);

  // Klik decyzji w banerze procesu, gdy formularz jest w edycji: najpierw ten sam zapis co
  // „Zapisz", okno decyzji dopiero po jego sukcesie (useSaveBeforeDecision; „zapis już trwa"
  // sprawdza sam hak po kluczu `saveMutationKey` mutacji zapisu tej kopii formularza).
  // `succeeded` / `aborted` są wołane z `saveMutation.onSuccess/onError` i z anulowania
  // ostrzeżenia kontroli niżej.
  const saveBeforeDecision = useSaveBeforeDecision({
    editing,
    recordId: id,
    save: handleSave,
  });

  // Kontrola kartoteki (procedura tenanta `control`, slot recordType='contractor') —
  // `mutate` celowo owinięty w lambdę: `saveMutation` deklarowana niżej, ale to
  // wywołanie trafia dopiero do zamknięcia `handleConfirm`, uruchamianego po pełnym
  // mount'cie (mirror PurchaseInvoiceDetail.tsx).
  // Ścieżka „zapisz mimo ostrzeżenia" celowo POMIJA format-gate z `handleSave` — dochodzi
  // do niej dopiero po pierwszym submit, który gate już przeszedł, a formularz jest zamrożony
  // za modalem kontroli, więc format nie mógł się zepsuć.
  const controlWarning = useControlWarning<ContractorFormData & { warningsAccepted?: boolean }>({
    mutate: (payload) => saveMutation.mutate(payload),
    // Anulowane ostrzeżenie kontroli = zapis się nie odbył → okno decyzji się nie otwiera, a zadanie
    // przycisku SQL kliknięte na nowym rekordzie nie rusza.
    onCancel: () => {
      saveBeforeDecision.aborted();
      settlePendingCreateRun();
    },
  });

  const saveMutation = useMutation({
    mutationKey: saveBeforeDecision.saveMutationKey,
    mutationFn: async (data: ContractorFormData & { warningsAccepted?: boolean }) => {
      // Atomic-save refactor (mirror ContractDetail/SaleInvoiceDetail): jeden
      // zapis — extValues + delty tabel dodatkowych jadą w TYM SAMYM body co
      // pola kontrahenta (backend zapisuje wszystko w jednej transakcji, patrz
      // saveAggregate). Pola opcjonalne — pomijamy gdy puste, zachowanie
      // identyczne jak dotąd dla callerów bez nich.
      const payload: ContractorFormData & {
        warningsAccepted?: boolean;
        extValues?: Record<string, unknown>;
        additionalTables?: FlattenedAdditionalTables;
        additionalParentValues?: AdditionalParentValues;
      } = { ...data };
      // Migawka edycji tuż przed włożeniem `extValues` do żądania — PRZED oczekiwaniem na
      // definicje pól tabel (`buildAdditionalParentValues`): edycja zrobiona w tym czasie nie
      // trafia do body, więc `markSaved()` w onSuccess jej nie zdejmie (nagłówek `useRecordExtValues.ts`).
      snapshotEdits();
      if (Object.keys(extValues).length > 0) {
        payload.extValues = extValues;
      }
      const additionalTablesBody = flattenAdditionalDeltas();
      if (additionalTablesBody) {
        payload.additionalTables = additionalTablesBody;
        // Wartości pól kontrahenta z ekranu dla wiązań `parent_field` w wierszach tabel
        // dodatkowych — OBOK delt (nie w nich), tylko slugi użyte przez wiązania, tylko skalary.
        const additionalParentValues = await buildAdditionalParentValues(
          buildAdditionalViewRecordValues(extValues, buildContractorCoreRecordValues(data)),
          'default',
        );
        if (additionalParentValues) {
          payload.additionalParentValues = additionalParentValues;
        }
      }

      let recordId: string;
      let processAutoStarting = false;
      if (isCreate) {
        // CORE INVARIANT: w trybie „Wszystkie firmy" interceptor NIE wysyła X-Company-Id
        // (brak aktywnej firmy) — bez jawnego nagłówka backend (`requireContractorVisibility`
        // → `requireCompany`) odrzuciłby POST z MISSING_HEADER, ZANIM cokolwiek zajrzy do
        // `targetCompanyId` w body (mirror `PurchaseInvoiceDetail.tsx`/innych klas dokumentów —
        // ten sam CORE INVARIANT komentarz tam). Jawny nagłówek per-request wygrywa z
        // interceptorem (`lib/api.ts` nie nadpisuje już ustawionego X-Company-Id) i działa
        // identycznie w trybie jednej aktywnej firmy (nadpisuje tą samą wartością).
        const res = await api.post<{ contractor: { id: string }; processAutoStarting?: boolean }>(
          `/contractors`,
          payload,
          data.targetCompanyId ? { headers: { 'X-Company-Id': data.targetCompanyId } } : undefined,
        );
        recordId = res.data.contractor.id;
        processAutoStarting = res.data.processAutoStarting ?? false;
      } else {
        await api.patch(`/contractors/${id}`, payload);
        recordId = id!;
      }
      return { contractor: { id: recordId }, processAutoStarting };
    },
    onSuccess: async (data, variables) => {
      // Udany zapis gasi podświetlenia pól z POPRZEDNIEJ nieudanej próby (np. gdy
      // po odrzuconej walidacji user poprawił dane i zapisał) — inaczej czerwona
      // ramka + komunikat zostają na ekranie do przeładowania karty. Pierwsza
      // instrukcja, przed każdą gałęzią z wczesnym `return` (create embedded/navigate).
      fe.clearAll();
      // Pola dodatkowe zapisane razem z kontrahentem — od tej chwili wartość serwera (z odświeżonego
      // rekordu) może je nadpisać; bez tego guard niezapisanych edycji trzymałby stare wartości.
      markSaved();
      queryClient.invalidateQueries({ queryKey: ['contractors'] });
      // Atomic-save refactor: backend już zapisał tabele dodatkowe atomowo —
      // odśwież rekordy (prefix match łapie wszystkie
      // ['additional-records', tableId, parentId]) i wyczyść lokalny bufor delt.
      queryClient.invalidateQueries({ queryKey: ['additional-records'] });
      resetAdditionalDeltas();
      // Finding [G] (pre-commit-verify): szkic niezatwierdzonego add-row też czyścimy
      // po udanym zapisie — spójnie z buforem zatwierdzonych wierszy powyżej.
      resetAddRowDrafts();

      if (isCreate && data?.contractor?.id) {
        toast.show({ variant: 'success', title: t('tenant.contractors.toast.created') });
        // Przycisk SQL kliknięty na nowym rekordzie: zadanie rusza TERAZ — z id nowego kontrahenta,
        // PRZED przejściem pod jego adres (albo oddaniem id oknu osadzonemu, które zwykle się zamyka).
        // Porażka zlecenia dostaje własny komunikat; rekord już istnieje, więc przejście i tak następuje.
        // Zlecenie niesie tę samą firmę docelową, którą POST tworzący wysłał jawnie (`variables`).
        if (pendingCreateRunRef.current !== null) {
          await runPendingCreateRun(data.contractor.id, variables.targetCompanyId);
        }
        // Tryb osadzony: nie nawigujemy — oddajemy id nowego rekordu oknu-gospodarzowi
        // (picker zamyka modal i zaznacza świeżo utworzonego kontrahenta).
        if (embedded) {
          embedded.onSaved(data.contractor.id);
          return;
        }
        // Karta „nowy" ma stać się kartą zapisanego rekordu (silnik kart zamienia ją w miejscu
        // po znaczniku `replaceTab`), a nie zostać sierotą obok nowej karty edycji. Reset do
        // trybu podglądu jest konieczny, bo create→detail NIE remontuje strony (ten sam element
        // w trasie, bez `key` — patrz komentarz przy useRecordProcessAutostart wyżej), więc
        // `editing` (wartość początkowa) inaczej zostałby na `true` i zapisany rekord pokazałby
        // się w trybie edycji zamiast podglądu.
        setEditing(false);
        navigate(withMenuItem(`/contractors/${data.contractor.id}`), {
          state: {
            replaceTab: true,
            ...(data.processAutoStarting ? { processAutoStarting: true } : {}),
          },
        });
        return;
      }
      toast.show({ variant: 'success', title: t('tenant.contractors.toast.updated') });
      // Spójność adresu macierzystego: zapis „Dane" zmienia adres główny (ten sam wiersz,
      // co lista „Adresy"), więc odświeżamy też listę adresów kontrahenta.
      queryClient.invalidateQueries({ queryKey: ['addresses', 'contractor', id] });
      // Zapis mógł zmienić kategorię kontrahenta (a od niej zależą prawa obiegu): flagi „Wyślij do
      // akceptacji" / „Anuluj proces" odświeżamy RAZEM z rekordem (bez czekania na nie).
      void invalidateRecordInstances(queryClient, CONTRACTOR_MODULE_CODE, data.contractor.id);
      // Awaitowane TYLKO odświeżenie samego rekordu: baner procesu bierze z niego kategorię, a
      // okno wysyłki do akceptacji (otwierane przez `succeeded()` niżej) prosi o listę procesów
      // dla BIEŻĄCEJ kategorii — bez czekania mogłoby się otworzyć z listą kategorii sprzed zapisu.
      // Reszta odświeżeń zostaje bez czekania.
      await queryClient.invalidateQueries({ queryKey: ['contractor', id] });
      // `setEditing(false)` i `succeeded()` razem, bez `await` między nimi (wymóg haka).
      setEditing(false);
      // Tryb osadzony (edit): powiadom okno-gospodarza, że rekord się zmienił (odśwież
      // cache nazwy/NIP na dokumencie). Modal zostawiamy otwarty — zamknięcie jest w
      // gestii usera (może chcieć dalej edytować inne zakładki).
      if (embedded && id) embedded.onSaved(id);
      // Zapis wywołany kliknięciem decyzji w banerze → teraz otwórz okno decyzji (no-op, gdy
      // zapis poszedł zwykłym „Zapisz"). Tylko gałąź edycji — przy tworzeniu nie ma banera.
      saveBeforeDecision.succeeded();
    },
    onError: (err) => {
      const code = extractErrorCode(err);
      // Zapis wywołany kliknięciem decyzji w banerze: każdy błąd poza ostrzeżeniem kontroli
      // kończy sprawę (okno decyzji się nie otworzy). O `CONTROL_WARNING` rozstrzyga user w
      // dialogu: potwierdzenie ponawia zapis (okno otworzy jego sukces), anulowanie woła
      // `onCancel`. Na samym początku, żeby żadna późniejsza gałąź (ani wyjątek w niej) nie
      // zostawiła czekającego okna.
      if (code !== 'CONTROL_WARNING') {
        saveBeforeDecision.aborted();
        // Zadanie przycisku SQL kliknięte na nowym rekordzie nie rusza, gdy zapis (utworzenie) się nie udał.
        settlePendingCreateRun();
      }
      // Podświetlenie pól odrzuconych przez serwer musi zobaczyć KAŻDĄ odpowiedź,
      // zanim jakakolwiek gałąź zrobi wczesny `return` — inaczej stare podświetlenie
      // z poprzedniej próby zostaje na ekranie przy zupełnie innym błędzie (patrz
      // JSDoc `useFieldErrors.ts`).
      // Kolejność komunikatu: (1) polski z issue Zoda, (2) własny komunikat serwera
      // z `details` (reguły krzyżowe / refine NIP-REGON-KRS mają tu gotowe polskie
      // zdanie i NIE mają `issues`), (3) generyk. Bez `messages[0]` polskie komunikaty
      // reguł serwera ginęłyby pod generykiem.
      const fieldMsgs = buildFieldIssueMessages(err, t);
      fe.applyServerError(err, {
        translate: (field, messages) =>
          fieldMsgs[field] ?? messages[0] ?? t('common.validationIssue.generic'),
      });
      // Kontrola kartoteki (CONTROL_ERROR/CONTROL_WARNING/CONTROL_HOOK_FAILED) ma
      // pierwszeństwo — jeśli obsłużona (toast lub dialog już pokazany), reszta
      // gałęzi (specyficzne dla kontrahenta) nie musi się odpalać.
      if (controlWarning.handleError(err, form)) return;
      // Zapis odrzucony, bo na rekordzie trwa zadanie przycisku SQL — czytelny komunikat z opisem zadania
      // zamiast ogólnego „nie udało się zapisać”; odświeżony stan zadań pokaże pasek blokady.
      if (runs.handleLockedError(err)) return;
      const data = isAxiosError(err) ? (err.response?.data as Record<string, unknown>) : null;
      if (code === 'NIP_TAKEN' && data) {
        setNipConflict({
          contractorId: String(data.existingContractorId ?? ''),
          name: form.name || (data.existingContractorId as string) || '',
          homeCompanyName: String(data.existingHomeCompanyName ?? ''),
          hasAccessFromCurrentCompany: Boolean(data.hasAccessFromCurrentCompany),
        });
        setNipModalOpen(true);
        toast.show({ variant: 'warning', title: t('tenant.contractors.errors.nipTaken') });
        return;
      }
      // Walidacja, słowniki, opiekun/handlowiec, skrót, brak prawa — ten sam komunikat co przy
      // przeciągnięciu karty na tablicy Kanban (wspólny helper).
      toast.show({
        variant: 'error',
        title:
          contractorSaveErrorMessage(err, t, contractorFieldLabel) ??
          t('tenant.contractors.toast.saveError'),
      });
    },
  });

  // Kontrola kartoteki (F20+ reużywalny hook) dla flow DELETE — osobne klucze i18n
  // ("Usuń mimo to") niż flow SAVE, patrz useControlWarning.
  const deleteControlWarning = useControlWarning<{ warningsAccepted?: boolean }>({
    mutate: (payload) => deleteMutation.mutate(payload),
    titleKey: 'tenant.documents.controlWarning.deleteTitle',
    confirmLabelKey: 'tenant.documents.controlWarning.deleteConfirmLabel',
  });

  const deleteMutation = useMutation({
    mutationFn: async (variables: { warningsAccepted?: boolean } = {}) => {
      const query = variables.warningsAccepted ? '?warningsAccepted=true' : '';
      await api.delete(`/contractors/${id}${query}`);
    },
    onSuccess: () => {
      toast.show({ variant: 'success', title: t('tenant.contractors.toast.deleted') });
      queryClient.invalidateQueries({ queryKey: ['contractors'] });
      // Tryb osadzony: zamknij okno zamiast nawigować na listę.
      if (embedded) {
        embedded.onCancel();
        return;
      }
      navigate(withMenuItem('/contractors'));
    },
    onError: (err, variables) => {
      if (deleteControlWarning.handleError(err, variables ?? {})) return;
      // Usuwanie odrzucone na czas zadania przycisku SQL — jak przy zapisie (opis zadania + pasek blokady).
      if (runs.handleLockedError(err)) return;
      toast.show({ variant: 'error', title: t('tenant.contractors.toast.deleteError') });
    },
  });

  // Usunięcie powiązania synchronizacji z Symfonią (per firma) — osobne od „wyłącz z synchronizacji".
  const unlinkMutation = useMutation({
    mutationFn: async () => {
      await api.post(
        `/contractors/${id}/remove-symfonia-link`,
        {},
        form.symfoniaCompanyId
          ? { headers: { 'X-Company-Id': form.symfoniaCompanyId } }
          : undefined,
      );
    },
    onSuccess: () => {
      toast.show({ variant: 'success', title: t('tenant.contractors.detail.symfonia.unlinked') });
      setConfirmUnlink(false);
      queryClient.invalidateQueries({ queryKey: ['contractor', id] });
      queryClient.invalidateQueries({ queryKey: ['contractors'] });
    },
    onError: (err) => {
      // Odpięcie odrzucone na czas zadania przycisku SQL — opis zadania + pasek blokady.
      if (runs.handleLockedError(err)) return;
      toast.show({
        variant: 'error',
        title: t('tenant.contractors.detail.symfonia.unlinkError'),
      });
    },
  });

  /** Kończy oczekiwanie przycisku SQL klikniętego na nowym rekordzie bez uruchamiania zadania. */
  function settlePendingCreateRun(): void {
    const pending = pendingCreateRunRef.current;
    pendingCreateRunRef.current = null;
    pending?.settle();
  }

  /**
   * Zleca zadanie zapamiętane przy kliknięciu na nowym rekordzie — z id świeżo utworzonego kontrahenta i z
   * firmą docelową z formularza (nagłówek firmy jak w POST tworzącym; w trybie „Wszystkie firmy" aktywnej
   * firmy aplikacji nie ma). W oknie osadzonym (zamyka się zaraz po utworzeniu, więc nie pokaże stanu
   * zadania) przyjęcie zlecenia ogłasza komunikat.
   */
  async function runPendingCreateRun(
    newContractorId: string,
    targetCompanyId: string | undefined,
  ): Promise<void> {
    const pending = pendingCreateRunRef.current;
    pendingCreateRunRef.current = null;
    if (pending === null) return;
    try {
      const run = await runs.startRun(
        pending.procedureId,
        newContractorId,
        targetCompanyId || undefined,
      );
      if (run !== null && embedded) {
        toast.show({
          variant: 'info',
          title: t('recordProcedureRuns.toast.started', { label: pending.label }),
        });
      }
    } finally {
      pending.settle();
    }
  }

  /**
   * Start zadania przycisku SQL. W edycji i podglądzie wołany PO bramce zapisu (edycja: najpierw zwykły
   * zapis, błąd zapisu = zadanie nie startuje; podgląd: od razu). Na nowym rekordzie zapamiętuje
   * zadanie i uruchamia zwykły zapis (utworzenie) — resztę robi `saveMutation.onSuccess`.
   */
  function handleSqlButtonStart(procedureId: string, label: string): Promise<unknown> {
    if (!isCreate) return runs.startRun(procedureId);
    // Utworzenie już trwa (kliknięte „Zapisz" albo wcześniejszy przycisk) — drugi POST utworzyłby kontrahenta
    // drugi raz. Twardy bezpiecznik: stan mutacji czytany na żywo (`useIsMutating` odświeża się dopiero po
    // renderze, a kliknięcie może wyprzedzić render).
    if (
      queryClient.isMutating({ mutationKey: saveBeforeDecision.saveMutationKey, exact: true }) > 0
    ) {
      return Promise.resolve();
    }
    return new Promise<void>((resolve, reject) => {
      pendingCreateRunRef.current = { procedureId, label, settle: resolve };
      try {
        if (!handleSave()) settlePendingCreateRun();
      } catch (err) {
        // Wyjątek zanim zapis ruszył: zapamiętane zadanie nie może zostać w refie (wystartowałoby po
        // następnym, zwykłym zapisie) — czyścimy je i odrzucamy obietnicę (przycisk wraca do gotowości).
        pendingCreateRunRef.current = null;
        reject(err);
      }
    });
  }

  function handleCancel() {
    // Anulowanie gasi podświetlenia z nieudanej próby zapisu (ramka + tekst pod polem).
    fe.clearAll();
    // Tryb osadzony (okno wyboru): „Anuluj" ZAMYKA okno — zarówno w create, jak i w edit.
    // W modalu użytkownik wszedł, żeby dodać/edytować; anulowanie = zamknięcie zadania
    // (a nie zejście do trybu podglądu wewnątrz okna, jak na stronie). Zakładki
    // self-persist (adresy/konta/kontakty) zapisały się już wcześniej, więc nic nie giniemy.
    if (embedded) {
      embedded.onCancel();
      return;
    }
    if (isCreate) {
      navigate(withMenuItem('/contractors'));
      return;
    }
    setEditing(false);
    discardEdits();
    // Anuluj porzuca też zbuforowane zmiany tabel dodatkowych i szkice add-row (nie tylko zapis).
    resetAdditionalDeltas();
    resetAddRowDrafts();
    if (detailQ.data?.contractor) {
      const c = detailQ.data.contractor;
      setForm({
        name: c.name,
        // Read-only na formularzu edycji (patrz renderer `company_id` — disabled poza
        // `isCreate`); wypełnione, żeby CompanyPicker pokazywał AKTUALNĄ firmę-właściciela.
        targetCompanyId: c.homeCompanyId,
        shortName: c.shortName,
        objectType: c.objectType,
        taxId: c.taxId,
        vatEu: c.vatEu,
        regon: c.regon,
        krs: c.krs,
        status: c.status,
        email: c.email,
        phone: c.phone,
        mobile: c.mobile,
        website: c.website,
        currencyCode: c.currencyCode,
        paymentTermsDays: c.paymentTermsDays,
        creditLimit: c.creditLimit,
        isVatPayer: c.isVatPayer,
        vatStatus: c.vatStatus,
        ownerUserId: c.ownerUserId,
        assignedUserId: c.assignedUserId,
        source: c.source,
        syncExcluded: c.syncExcluded ?? false,
        symfoniaExternalId: c.symfoniaExternalId ?? null,
        symfoniaPosition: c.symfoniaPosition ?? null,
        symfoniaActive: c.symfoniaActive ?? null,
        symfoniaCompanyId: c.symfoniaCompanyId ?? null,
        industry: c.industry,
        preferredLanguage: c.preferredLanguage ?? 'pl',
        gdprConsentAt: c.gdprConsentAt ?? null,
        gdprMarketingConsent: c.gdprMarketingConsent ?? false,
        gdprDataRetentionUntil: c.gdprDataRetentionUntil ?? null,
        notes: c.notes,
        tags: c.tags ?? [],
        pkdCodes: c.pkdCodes ?? [],
        groups: c.groups ?? [],
        categoryId: c.categoryId ?? null,
        primaryAddress: detailQ.data.primaryAddress
          ? {
              street: detailQ.data.primaryAddress.street,
              buildingNo: detailQ.data.primaryAddress.buildingNo,
              apartmentNo: detailQ.data.primaryAddress.apartmentNo,
              postalCode: detailQ.data.primaryAddress.postalCode,
              city: detailQ.data.primaryAddress.city,
              region: detailQ.data.primaryAddress.region,
              countryCode: detailQ.data.primaryAddress.countryCode,
            }
          : { ...EMPTY_PRIMARY_ADDRESS },
      });
    }
  }

  // Core invariant — mirror EmployeeDetail.tsx: blokuje submit w trybie create dopóki
  // `targetCompanyId` nie jest ustawiony (prefillowany automatycznie w trybie konkretnej
  // firmy, wymaga jawnego pick w trybie "Wszystkie firmy" — patrz efekt prefill wyżej +
  // renderer `company_id` w `contractorEngineConfig.tsx`). Podświetlenie pola (czerwona
  // etykieta + tekst pod pickerem, przez `fe`/`getError` — ten sam kanał co błędy
  // walidacji serwera) idzie obok toastu, nie zamiast niego.
  // Zwraca, czy zapis wystartował (`false` = zatrzymała go walidacja w panelu) — z tego korzysta
  // `useSaveBeforeDecision` (klik decyzji w banerze w edycji = najpierw ten sam zapis).
  function handleSave(): boolean {
    if (isCreate && !form.targetCompanyId) {
      fe.set('targetCompanyId', t('common.companyRequired'));
      toast.show({ variant: 'error', title: t('common.companyRequired') });
      return false;
    }
    fe.clear('targetCompanyId');

    // Format wg TYPU pola (wspólna oś silnika): e-mail/URL/liczba/data itd. dla pól
    // core (config, po stateKey) i dodatkowych (katalog, po slugu). Puste pola
    // przepuszcza sam walidator — obecność to osobna reguła (serwer/company guard).
    const fieldErrors = collectEngineFieldErrors(
      contractorEngineConfig,
      form,
      kartotekaFields,
      extValues,
      t,
    );
    if (Object.keys(fieldErrors).length > 0) {
      fe.clearAll();
      for (const [key, msg] of Object.entries(fieldErrors)) {
        fe.set(key, msg);
      }
      toast.show({ variant: 'error', title: t('common.validationIssue.formToast') });
      return false;
    }

    saveMutation.mutate(form);
    return true;
  }

  if (!isCreate && detailQ.isLoading) {
    return (
      <div className="flex items-center justify-center py-10" role="status">
        <Spinner />
      </div>
    );
  }

  if (!isCreate && detailQ.isError) {
    return (
      <div
        role="alert"
        className="m-6 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
      >
        {t('tenant.contractors.detail.loadingError')}
      </div>
    );
  }

  // Nowy kontrahent: pola dopiero po wczytaniu wartości domyślnych (albo komunikat z „Spróbuj
  // ponownie”). Po wszystkich hookach.
  if (isCreate && recordDefaults.status !== 'ready') {
    return <NewRecordDefaultsStatus defaults={recordDefaults} testIdPrefix="contractor-detail" />;
  }

  const homeCompanyName = detailQ.data?.homeCompanyName ?? '';
  // Rekord zablokowany na czas zadania przycisku SQL (decyzja „A”): aktywne zadanie albo właśnie
  // wysyłane zlecenie — „Edytuj” i „Usuń” nieaktywne (serwer odrzuca zapis i usunięcie tak czy inaczej).
  const recordLocked = runs.active !== null || runs.starting;

  // Mapa rendererów elementów zależnych od kontekstu — wspólna dla silnika (zwykłe
  // zakładki) i dedykowanej zakładki „Dane" (ContractorDataTab), żeby element „widokowy"
  // (powiązania/załączniki/…) postawiony w „Dane" renderował się żywcem, nie jako
  // martwy placeholder. Do czasu przejścia kartoteki w całości na silnik.
  const contractorContextRenderers: ContextRenderers = {
    // Atomic-save refactor (mirror ContractDetail/buildDocumentAdditionalRenderers):
    // tabele dodatkowe buforowane — dostępne już w trybie create (bez HTTP),
    // delty jadą w POST/PATCH razem z resztą body. `getBufferedValue` czyta
    // AKTUALNĄ wartość z bufora hooka przy każdym renderze, więc remount
    // panelu (np. Tabs przełączony i z powrotem) nie zeruje bufora (Krok 7 bugfix,
    // patrz nagłówek AdditionalTableTab.tsx). Kontrahent nie jest klasą dokumentu,
    // więc nie idzie przez `buildDocumentAdditionalRenderers` (parentTableName tam
    // jest zahardkodowane na 'documents') — wiring analogiczny, ale lokalny.
    // Tabela dodatkowa zajmuje całą szerokość sekcji (`col-span-full`) — tak samo jak w
    // podglądzie kreatora formatki; bez tego trafiłaby w jedną komórkę dwukolumnowej siatki.
    additional_view: ({ element }) => {
      const tableSlug =
        typeof element?.settings?.tableSlug === 'string' ? element.settings.tableSlug : '';
      return tableSlug ? (
        <div className="col-span-full">
          <AdditionalTableTab
            tableSlug={tableSlug}
            parentId={id ?? null}
            editable={editing}
            buffered
            onDeltasChange={handleAdditionalDeltasChange}
            getBufferedValue={getBufferedValue}
            getAddRowDraft={getAddRowDraft}
            onAddRowDraftChange={handleAddRowDraftChange}
            parentValues={additionalOwnerValues}
            parentKey="default"
            columnSettings={element.settings}
          />
        </div>
      ) : null;
    },
    satellite_view: ({ element }) => {
      const tableSlug =
        typeof element?.settings?.tableSlug === 'string' ? element.settings.tableSlug : '';
      return tableSlug ? (
        <div className="col-span-full">
          <AdditionalTableTab
            tableSlug={tableSlug}
            parentId={id ?? null}
            editable={editing}
            buffered
            onDeltasChange={handleAdditionalDeltasChange}
            getBufferedValue={getBufferedValue}
            getAddRowDraft={getAddRowDraft}
            onAddRowDraftChange={handleAddRowDraftChange}
            parentValues={additionalOwnerValues}
            parentKey="default"
            columnSettings={element.settings}
          />
        </div>
      ) : null;
    },
    // F22 (widoki dodatkowe) — sekcja read-only zasilana SQL. Kontrahent nie jest klasą
    // dokumentu (nie idzie przez `buildDocumentAdditionalRenderers`), wiring lokalny — mirror
    // struktury `additional_view`/`satellite_view` powyżej. recordValues: ext-fields (`extValues`,
    // żywe, działa w create) SCALONE z polami core (`buildContractorCoreRecordValues(form)`,
    // słownik slug→stateKey w `ContractorDataTab.tsx`) — wiązania `record_field` działają zarówno
    // na pola dodatkowe, jak i na pola core kontrahenta. `context.company`/`context.user`/
    // `constant` działają bez zmian (rozwiązuje je serwer).
    additional_query_view: ({ element }) => {
      const viewSlug =
        typeof element?.settings?.viewSlug === 'string' ? element.settings.viewSlug : '';
      return viewSlug ? (
        <div className="col-span-full">
          <AdditionalViewTab
            viewSlug={viewSlug}
            recordId={id ?? null}
            recordValues={additionalOwnerValues}
            columnSettings={element.settings}
          />
        </div>
      ) : null;
    },
    // Fala B: tabelka załączników kartoteki kontrahenta — widoczna tylko
    // dla zapisanego rekordu (self-persisting sub-resource, jak contacts/
    // addresses/bank_accounts). companyId z tego samego źródła co
    // ExistingContractorModal (homeCompanyId zapisanego rekordu, fallback
    // na aktywny kontekst firmy w trybie "Wszystkie firmy").
    attachments_view: () =>
      id ? (
        <AttachmentsSection
          ownerType="contractor"
          ownerId={id}
          companyId={existingContractorCompanyId ?? undefined}
        />
      ) : null,
    // Adres macierzysty jako JEDNA formatka (Model B v1) — wszystkie pola w jednym bloku,
    // stała kolejność, ukrywanie pól z konfiguracji elementu (`element.settings.hiddenFields`),
    // Województwo tylko dla Polski. Bufor `form.primaryAddress` (działa w create), zapis do
    // tabeli adresów razem z kontrahentem; GUS/NIP autofill wypełnia go z MF.
    contractor_primary_address_view: ({ element }) => (
      <ContractorPrimaryAddressSection
        value={form.primaryAddress}
        onChange={(next) => setForm((f) => ({ ...f, primaryAddress: next }))}
        editable={editing}
        hiddenFields={parseHiddenPrimaryAddressFields(element.settings)}
        fieldOrder={parseSlugList(element.settings, 'fieldOrder')}
      />
    ),
    ...buildDocumentPreviewContextRenderer({
      ownerType: 'contractor',
      ownerId: id,
      companyId: existingContractorCompanyId ?? undefined,
      extValues,
    }),
    // F.RL (powiązania między kartotekami) — blok self-persisting, niezależny
    // od atomic-save formularza. Wymaga zapisanego rekordu (brak w trybie create).
    // `element.settings` — per-placement config (allowAdd/allowUnlink/allowedLinkTypeIds/
    // allowedTargetTypes/defaultLinkTypeId) ustawiony w builderze, parsowany defensywnie.
    record_links_view: ({ element }) =>
      id ? (
        <RecordLinksSection
          ownerType="contractor"
          ownerId={id}
          companyId={activeCompanyScope.companyId}
          config={parseRecordLinksElementConfig(element.settings)}
        />
      ) : null,
    // F17 Commit B: klocek buildera „Proces akceptacji" — mirror gatingu seedowanej
    // zakładki `approvals` (patrz tabRenderers.approvals niżej): wymaga zapisanego
    // rekordu I aktywnego modułu Procesy. Bez aktywnego modułu — cichy `null`
    // (element po prostu nie renderuje treści na tej stronie), zamiast dublować
    // komunikat "moduł nieaktywny" w dowolnym miejscu formularza, gdzie tenant go postawi.
    // Ustawienia klocka: `showCancelled` i `showDecisionActions` (przyciski decyzji z tą samą
    // bramką zapisu co baner).
    process_view: ({ element }) => {
      if (!id || !processesActive) return null;
      const cfg = parseProcessViewElementConfig(element.settings);
      return (
        <ProcessHistoryTab
          moduleCode={CONTRACTOR_MODULE_CODE}
          recordId={id}
          showCancelled={cfg.showCancelled}
          actions={
            cfg.showDecisionActions ? { beforeDecision: saveBeforeDecision.beforeDecision } : null
          }
        />
      );
    },
    // Klocek „Przycisk akceptacji”: to samo miejsce co pasek akceptacji nad zakładkami — kategoria
    // kontrahenta i bramka zapisu te same co u `ProcessApprovalBanner` niżej; gating jak `process_view`.
    process_action_button: () => {
      if (!id || !processesActive) return null;
      return (
        <ProcessActionButton
          moduleCode={CONTRACTOR_MODULE_CODE}
          recordId={id}
          categoryId={detailQ.data?.contractor?.categoryId ?? null}
          beforeDecision={saveBeforeDecision.beforeDecision}
        />
      );
    },
    // Klocek „Przycisk SQL”: zadanie w tle na tym kontrahencie. W edycji klik najpierw zapisuje rekord
    // (ta sama bramka co przycisk akceptacji; błąd zapisu = zadanie nie rusza), w podglądzie rusza od
    // razu, na nowym rekordzie — utworzenie, zadanie, przejście (bez bramki: ta dotyczy edycji istniejącego).
    // Prawo: ten sam sygnał co „Edytuj” (serwer i tak sprawdza). Klocek bez procedury / napisu: nic.
    // „Zapisz" w toku (utworzenie / edycja) też blokuje przycisk — drugi klik nie może wysłać drugiego zapisu.
    // Identyfikatory testowe per klocek (`sql-button-<id klocka>`): strona może mieć kilka przycisków.
    sql_button: ({ element }) => {
      const cfg = parseSqlButtonSettings(element.settings);
      if (cfg === null) return null;
      return (
        <SqlButton
          label={cfg.label}
          procedureId={cfg.procedureId}
          active={runs.active}
          lastRun={runs.lastByProcedure.find((run) => run.procedureId === cfg.procedureId)}
          canEdit={canEditRecord}
          starting={runs.starting || saveMutation.isPending}
          beforeStart={isCreate ? undefined : saveBeforeDecision.beforeDecision}
          onStart={(procedureId) => handleSqlButtonStart(procedureId, cfg.label)}
          testIdPrefix={`sql-button-${element.id}`}
        />
      );
    },
    // Konfigurowalne zakładki — „Kontakty kontrahenta" jako przenośny blok
    // (wcześniej zaszyte tabRenderers.contacts). `element.settings` niesie config
    // kolumn subcollection z buildera (jak dawniej `subcollectionElement`).
    // Wymaga zapisanego rekordu — w trybie create pokazuje „najpierw zapisz".
    contractor_contacts_view: ({ element }) =>
      id ? (
        <ContactsTab contractorId={id} layoutElement={element} />
      ) : (
        <SaveFirstNotice message={t('tenant.contractors.detail.tabDisabled.save')} />
      ),
    // Konfigurowalne zakładki — „Adresy kontrahenta" jako przenośny blok. `settings.hiddenColumnIds`
    // (ikonka oka w formatce bloku) filtruje kolumny listy adresów.
    contractor_addresses_view: ({ element }) =>
      id ? (
        <AddressesDynamicTab
          ownerType="contractor"
          ownerId={id}
          testIdPrefix="contractor-addresses"
          hiddenColumnIds={parseHiddenAddressColumns(element.settings)}
          columnOrder={parseSlugList(element.settings, 'columnOrder')}
        />
      ) : (
        <SaveFirstNotice message={t('tenant.contractors.detail.tabDisabled.save')} />
      ),
    // Konfigurowalne zakładki — „Konta bankowe" jako przenośny blok. `settings.hiddenColumnIds`
    // (slugi ukrytych kolumn, ustawiane ikonką oka w formatce bloku) filtrują kolumny listy.
    contractor_bank_accounts_view: ({ element }) =>
      id ? (
        <BankAccountsDynamicTab
          contractorId={id}
          taxId={form.taxId}
          testIdPrefix="contractor-bank-accounts"
          hiddenColumnIds={parseHiddenBankAccountColumns(element.settings)}
          columnOrder={parseSlugList(element.settings, 'columnOrder')}
        />
      ) : (
        <SaveFirstNotice message={t('tenant.contractors.detail.tabDisabled.save')} />
      ),
    // Konfigurowalne zakładki — „Dostęp firm" jako przenośny blok.
    contractor_company_access_view: () =>
      id ? (
        <ContractorAccessTab contractorId={id} />
      ) : (
        <SaveFirstNotice message={t('tenant.contractors.detail.tabDisabled.save')} />
      ),
    // Kanoniczny element subcollection silnika: front (loadTabsFromConfig/saveTabsToConfig)
    // i builder normalizują zakładki subcollection do `related_records_table` (kolekcja w
    // settings). Dispatch po kolekcji na ten sam komponent, co nazwane bloki powyżej — dzięki
    // temu Kontakty/Adresy/Konta/Dostęp renderują się niezależnie, czy layout ma typ nazwany
    // (świeży seed) czy generyczny (builder / layout zmigrowany ze starego kształtu).
    related_records_table: ({ element }) => {
      if (!id) {
        return <SaveFirstNotice message={t('tenant.contractors.detail.tabDisabled.save')} />;
      }
      const collection =
        typeof element.settings?.collection === 'string' ? element.settings.collection : '';
      switch (collection) {
        case 'contractor_contacts':
          return <ContactsTab contractorId={id} layoutElement={element} />;
        case 'addresses':
          return (
            <AddressesDynamicTab
              ownerType="contractor"
              ownerId={id}
              testIdPrefix="contractor-addresses"
            />
          );
        case 'contractor_bank_accounts':
        case 'bank_accounts':
          return (
            <BankAccountsDynamicTab
              contractorId={id}
              taxId={form.taxId}
              testIdPrefix="contractor-bank-accounts"
            />
          );
        case 'contractor_company_access':
        case 'company_access':
          return <ContractorAccessTab contractorId={id} />;
        default:
          return null;
      }
    },
    // Konfigurowalne zakładki — „Historia zmian" jako przenośny element PER KARTOTEKA
    // (`contractor_audit_view`). Stary wspólny `audit_log_view` zostaje jako fallback dla
    // layoutów, które go jeszcze trzymają (ten sam read-side).
    contractor_audit_view: () =>
      id ? (
        <ContractorAuditTab contractorId={id} />
      ) : (
        <SaveFirstNotice message={t('tenant.contractors.detail.tabDisabled.save')} />
      ),
    audit_log_view: () =>
      id ? (
        <ContractorAuditTab contractorId={id} />
      ) : (
        <SaveFirstNotice message={t('tenant.contractors.detail.tabDisabled.save')} />
      ),
  };

  return (
    <DocumentPreviewDockHost
      enabled={previewDockEnabled && !!id}
      moduleId="contractors"
      ownerType="contractor"
      ownerId={id}
      companyId={existingContractorCompanyId ?? undefined}
    >
      <div className="p-6 space-y-4" data-testid="contractor-detail-page">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            {/* Strzałka wstecz — tylko na stronie. W trybie osadzonym okno ma własny
                przycisk zamknięcia (Modal), a nawigacja jest zastąpiona callbackami. */}
            {!embedded && (
              <IconButton
                aria-label={t('common.back')}
                data-testid="contractor-detail-back"
                onClick={() => navigate(withMenuItem('/contractors'))}
              >
                <ArrowLeft aria-hidden="true" />
              </IconButton>
            )}
            <div>
              {isCreate ? (
                // Tryb osadzony: tytuł „Nowy kontrahent" daje już nagłówek Modala —
                // nie dublujemy go w treści okna.
                embedded ? null : (
                  <h1
                    className="text-2xl font-semibold text-text-primary"
                    data-testid="contractor-detail-create-title"
                  >
                    {t('tenant.contractors.detail.createTitle')}
                  </h1>
                )
              ) : (
                <div className="flex items-center gap-3">
                  {id && (
                    <ContractorLogoEditor
                      contractorId={id}
                      logoUrl={detailQ.data?.contractor.logoUrl ?? null}
                    />
                  )}
                  <div>
                    <h1 className="text-2xl font-semibold text-text-primary">{form.name}</h1>
                    <div className="mt-1 flex items-center gap-3 text-xs text-text-tertiary">
                      <span className="rounded bg-gray-100 px-2 py-0.5 dark:bg-gray-800">
                        {t(`tenant.contractors.detail.status.${form.status}`)}
                      </span>
                      {form.taxId && (
                        <span>
                          {t('tenant.contractors.detail.nipLabel')}: {form.taxId}
                        </span>
                      )}
                      {homeCompanyName && (
                        <span data-testid="contractor-detail-home-company">{homeCompanyName}</span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {editing ? (
              <>
                <Button
                  variant="secondary"
                  onClick={handleCancel}
                  disabled={saveMutation.isPending}
                  leftIcon={<XIcon size={14} aria-hidden="true" />}
                  data-testid="contractor-detail-cancel-btn"
                >
                  {t('tenant.contractors.detail.cancel')}
                </Button>
                <Button
                  onClick={handleSave}
                  loading={saveMutation.isPending}
                  leftIcon={<Save size={14} aria-hidden="true" />}
                  data-testid="contractor-detail-save-btn"
                >
                  {t('tenant.contractors.detail.save')}
                </Button>
              </>
            ) : (
              <>
                {!isCreate && form.symfoniaExternalId && (
                  <Button
                    variant="secondary"
                    onClick={() => setConfirmUnlink(true)}
                    data-testid="contractor-detail-unlink-symfonia-btn"
                  >
                    {t('tenant.contractors.detail.symfonia.unlinkButton')}
                  </Button>
                )}
                {canDeleteRecord && (
                  <Button
                    variant="danger"
                    onClick={() => setConfirmDelete(true)}
                    disabled={recordLocked}
                    leftIcon={<Trash2 size={14} aria-hidden="true" />}
                    data-testid="contractor-detail-delete-btn"
                  >
                    {t('tenant.contractors.detail.delete')}
                  </Button>
                )}
                {canEditRecord && (
                  <Button
                    onClick={() => setEditing(true)}
                    disabled={recordLocked}
                    leftIcon={<Pencil size={14} aria-hidden="true" />}
                    data-testid="contractor-detail-edit-btn"
                  >
                    {t('tenant.contractors.detail.edit')}
                  </Button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Zadanie przycisku SQL na rekordzie (w kolejce lub w toku): rekordu nie da się zapisać ani
          usunąć, „Edytuj” i „Usuń” są nieaktywne; pasek znika po zakończeniu zadania. */}
        {runs.active ? (
          <RecordRunLockBanner
            run={runs.active}
            label={runs.labelFor(runs.active)}
            refreshFailed={runs.isError}
          />
        ) : null}

        {/* F17 T17.1.7/T17.1.8: UX akceptacji rekordu (start / przebieg / decyzja /
          anulowanie / historia) — jako banner nad zakładkami. Tylko dla istniejącego
          kontrahenta i gdy moduł Procesy jest aktywny. */}
        {!isCreate && id && processesActive ? (
          <div className="flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <ProcessApprovalBanner
                moduleCode={CONTRACTOR_MODULE_CODE}
                recordId={id}
                categoryId={detailQ.data?.contractor?.categoryId ?? null}
                beforeDecision={saveBeforeDecision.beforeDecision}
              />
            </div>
            {/* F17 follow-up — wskaźnik „proces się uruchamia…" (poll uzbrojony hintem
                z create; patrz useRecordProcessAutostart). Znika sam po pojawieniu się
                instancji albo po timeout — bez akcji usera. */}
            <ProcessAutostartIndicator starting={autostart.starting} />
          </div>
        ) : null}

        {/* Create=edit parity fix (mirror ContractDetail/SaleInvoiceDetail): create
          i edit współdzielą teraz RecursiveFormRenderer. Bez aktywnego layoutu
          (`!layoutConfig`) — sam ContractorDataTab (identycznie dla obu trybów).
          Z layoutem — RecursiveFormRenderer rendruje top-bar zakładek (z filtrem
          per-rola, sortem, hidden) + delegacja contentu zakładek do tabRenderers.
          Slugi w mapie tabRenderers to seed-slugi z `seed-system-modules.ts`.
          Zakładki wymagające zapisanego rekordu (contacts, addresses, bank_accounts,
          company_access, audit_log, approvals) zostają
          gated `!id` — w trybie create widoczne w tab-barze, ale bez treści (self-
          persisting sub-resources / placeholdery do-po-zapisie). Tabele dodatkowe
          (additional_view/satellite_view) są WYJĄTKIEM — buforowane, dostępne od
          razu bez HTTP, delty jadą w POST/PATCH razem z resztą body. */}
        {/* Pola z Symfonii (pozycja, status FK, wyłączenie z synchronizacji) NIE są już zaszytym
            banerem — są pełnoprawnymi polami katalogu (sekcja „Integracja Symfonia"), renderowanymi
            przez silnik w layoucie: przeciągalne w builderze, ukrywalne per layout/rola, kolumna listy.
            Pozycja i status = read-only (flaga `read_only` w katalogu), wyłączenie = edytowalne.
            Pozycja/status są PER FIRMA — GET zwraca wartość dla AKTYWNEJ firmy (X-Company-Id),
            magazyn per firma na `contractor_external_refs` (migracja 0139). */}
        {!layoutConfig ? (
          <ContractorDataTab
            form={form}
            onChange={setForm}
            editable={editing}
            contextRenderers={contractorContextRenderers}
            renderField={renderField}
            formData={layoutFormData}
          />
        ) : (
          <RecursiveFormRenderer
            layoutConfig={layoutConfig}
            activeTabId={tab}
            onTabChange={setTab}
            myRoleId={myRoleId}
            isSuperUser={isSuperUser}
            renderField={renderField}
            formData={layoutFormData}
            tabRenderers={{
              data: () => (
                <ContractorDataTab
                  form={form}
                  onChange={setForm}
                  editable={editing}
                  contextRenderers={contractorContextRenderers}
                  renderField={renderField}
                  formData={layoutFormData}
                />
              ),
              // Konfigurowalne zakładki: contacts/addresses/bank_accounts/company_access/
              // audit_log/approvals NIE są już zaszyte po slugu — renderują się jako
              // przenośne elementy przez contractorContextRenderers (można je ułożyć w
              // dowolnej zakładce). „Pola dodatkowe" usunięte (pojedyncze pola są
              // przeciągalne przez silnik). Stare drzewko kategorii (`categories`) usunięte
              // razem z całym mechanizmem contractor_categories.
            }}
            contextRenderers={contractorContextRenderers}
          />
        )}

        <ConfirmDialog
          open={confirmDelete}
          title={t('common.confirmDelete')}
          message={form.name}
          variant="danger"
          onConfirm={() => {
            setConfirmDelete(false);
            deleteMutation.mutate({});
          }}
          onClose={() => setConfirmDelete(false)}
        />

        {/* Usunięcie powiązania Symfonii — nie destrukcyjne dla kontrahenta (variant primary). */}
        <ConfirmDialog
          open={confirmUnlink}
          title={t('tenant.contractors.detail.symfonia.unlinkConfirmTitle')}
          message={t('tenant.contractors.detail.symfonia.unlinkConfirmBody')}
          variant="primary"
          confirmLabel={t('tenant.contractors.detail.symfonia.unlinkButton')}
          onConfirm={() => unlinkMutation.mutate()}
          onClose={() => setConfirmUnlink(false)}
        />

        {controlWarning.dialog}
        {deleteControlWarning.dialog}

        {existingContractorCompanyId && (
          <ExistingContractorModal
            open={nipModalOpen}
            match={nipConflict}
            onClose={() => setNipModalOpen(false)}
            companyId={existingContractorCompanyId}
          />
        )}

        {/* Onblur self-service share check (przeniesione z ContractorDataTab) — DRUGI,
          niezależny modal od 409 NIP_TAKEN powyżej (patrz komentarz przy `nipMatch`). */}
        {existingContractorCompanyId && (
          <ExistingContractorModal
            open={modalOpen}
            match={nipMatch}
            onClose={() => setModalOpen(false)}
            companyId={existingContractorCompanyId}
          />
        )}
      </div>
    </DocumentPreviewDockHost>
  );
}
